import { useState, useEffect } from 'react';
import { ConnectionStatus } from '@capacitor/network';
import { getNetworkStatus, isNative } from '@/lib/capacitor';

interface NetworkState {
  isOnline: boolean;
  connectionType: string;
}

/**
 * Hook to monitor network connectivity status
 */
export function useNetworkStatus(): NetworkState {
  const [state, setState] = useState<NetworkState>(() => {
    if (isNative) {
      const status = getNetworkStatus();
      return {
        isOnline: status.connected,
        connectionType: status.connectionType,
      };
    }
    // Web fallback
    return {
      isOnline: navigator.onLine,
      connectionType: 'unknown',
    };
  });

  useEffect(() => {
    if (isNative) {
      // Listen for Capacitor network events
      const handleNetworkChange = (event: CustomEvent<ConnectionStatus>) => {
        setState({
          isOnline: event.detail.connected,
          connectionType: event.detail.connectionType,
        });
      };

      window.addEventListener(
        'network-status-change',
        handleNetworkChange as EventListener
      );

      return () => {
        window.removeEventListener(
          'network-status-change',
          handleNetworkChange as EventListener
        );
      };
    } else {
      // Web fallback using navigator.onLine
      const handleOnline = () => setState((prev) => ({ ...prev, isOnline: true }));
      const handleOffline = () => setState((prev) => ({ ...prev, isOnline: false }));

      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);

      return () => {
        window.removeEventListener('online', handleOnline);
        window.removeEventListener('offline', handleOffline);
      };
    }
  }, []);

  return state;
}
